import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
  toastManager,
} from "@salestrak/ui";
import { useCallback, useState, type ReactNode } from "react";

import { editableShape, formVisibility, invalidDefaultItemIds, type Form, type FormsStore } from "#app/lib/forms";

export function useFormPublish(
  store: FormsStore,
  {
    onShare,
    onInvalidDefaults,
  }: {
    onShare?: (form: Form) => void;
    onInvalidDefaults?: (itemIds: string[]) => void;
  } = {},
): { publish: (form: Form) => void; dialog: ReactNode } {
  const [unshared, setUnshared] = useState<{
    form: Form;
    open: boolean;
  } | null>(null);

  const publishNow = useCallback(
    async (form: Form) => {
      const wasLive = form.status === "live";
      const outcome = await store.publishForm(form.formId);
      if (!outcome.ok) {
        toastManager.add({ title: outcome.message, type: "error" });
        return;
      }
      toastManager.add({
        title: wasLive ? "Changes published" : "Form published",
        description: wasLive
          ? "Everyone filling this form out sees the new questions."
          : "Your team can fill it out on a Project now.",
        type: "success",
      });
    },
    [store],
  );

  const publish = useCallback(
    (form: Form) => {
      const staged = editableShape(form);
      const invalid = invalidDefaultItemIds(staged.items);
      // Publish stays pressable and the press shows which question blocks it, never a greyed button.
      if (invalid.length > 0) {
        toastManager.add({
          title:
            invalid.length === 1
              ? "Fix 1 default value to publish"
              : `Fix ${invalid.length} default values to publish`,
          description: onInvalidDefaults
            ? undefined
            : "Open the form to fix it in the question's settings.",
          type: "error",
        });
        onInvalidDefaults?.(invalid);
        return;
      }
      const shares = form.sharedWith?.length ?? 0;
      if (form.status !== "live" && formVisibility(form) === "private" && shares === 0) {
        setUnshared({ form, open: true });
        return;
      }
      void publishNow(form);
    },
    [onInvalidDefaults, publishNow],
  );

  const dialog = (
    <AlertDialog
        onOpenChange={(open) => {
          if (!open) setUnshared((current) => current && { ...current, open: false });
        }}
        open={unshared?.open ?? false}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Nobody is shared on this form</AlertDialogTitle>
            <AlertDialogDescription>
              It is Private, so only whoever fills it and admins can open its submissions. Share it with the team that receives it first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose
              onClick={() => {
                if (unshared) void publishNow(unshared.form);
              }}
              render={<Button variant="outline" />}
            >
              Publish anyway
            </AlertDialogClose>
            {onShare ? (
              <AlertDialogClose
                onClick={() => {
                  if (unshared) onShare(unshared.form);
                }}
                render={<Button />}
              >
                Share first
              </AlertDialogClose>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogPopup>
    </AlertDialog>
  );

  return { dialog, publish };
}
